import { useEffect, useState } from 'react';
import { RefreshCw, Search, AlertCircle } from 'lucide-react';
import { apiService } from '../services/api';
import type {
  AdminSyncSummary, AdminSyncRunRow, AdminSyncUserRow, VerificationExplain,
} from '../services/api';

/**
 * Admin → Senkron Turları
 *
 * "Otomatik/manuel sipariş güncellemesi gerçekten işe yarıyor mu, hata var mı,
 * bir kullanıcının siparişi neden doğrulanmamış?" sorularının tek ekranı.
 *
 * Buradaki her sayı gerçek kayıttan gelir: turların kendi özetleri (SyncRuns) ve
 * siparişlerin güncel durumu. Tahmin/örnekleme yok.
 */

const fmtDate = (s?: string | null) =>
  s ? new Date(s).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

const outcomeStyle = (o: string) =>
  o === 'ok' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : o === 'partial' || o === 'account-changed' ? 'bg-amber-50 text-amber-700 border-amber-200'
      : o === 'no-target' ? 'bg-slate-50 text-slate-600 border-slate-200'
        : 'bg-red-50 text-red-700 border-red-200';

const outcomeLabel = (o: string) =>
  ({ ok: 'Tamamlandı', partial: 'Yarım kaldı', 'no-target': 'İş yoktu', login: 'Oturum yok', timeout: 'Süre doldu', error: 'Hata', 'account-changed': 'Hesap değişti' } as Record<string, string>)[o] || o;

const stateStyle = (s: string) =>
  s === 'verified' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : s === 'needs-update' ? 'bg-amber-50 text-amber-700 border-amber-200'
      : s === 'closed' ? 'bg-slate-50 text-slate-600 border-slate-200'
        : 'bg-blue-50 text-blue-700 border-blue-200';

export default function AdminSyncRuns() {
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState<AdminSyncSummary | null>(null);
  const [runs, setRuns] = useState<AdminSyncRunRow[]>([]);
  const [runsTotal, setRunsTotal] = useState(0);
  const [users, setUsers] = useState<AdminSyncUserRow[]>([]);
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // Sipariş sorgulama
  const [orderId, setOrderId] = useState('');
  const [explainUserId, setExplainUserId] = useState('');
  const [explain, setExplain] = useState<VerificationExplain | null>(null);
  const [explainErr, setExplainErr] = useState<string | null>(null);
  const [explainLoading, setExplainLoading] = useState(false);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const [s, r, u] = await Promise.all([
        apiService.getAdminSyncSummary(days),
        apiService.getAdminSyncRuns({ days, pageSize: 50, type: typeFilter || undefined }),
        apiService.getAdminSyncUsers(days, 50),
      ]);
      setSummary(s); setRuns(r.rows || []); setRunsTotal(r.total || 0); setUsers(u.rows || []);
    } catch (e: any) {
      setErr(e?.message || 'Veriler alınamadı');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [days, typeFilter]);

  const sorgula = async () => {
    const id = orderId.trim();
    if (!id) return;
    setExplainLoading(true); setExplainErr(null); setExplain(null);
    try {
      setExplain(await apiService.getVerificationExplain(id, explainUserId.trim() || undefined));
    } catch (e: any) {
      setExplainErr(e?.message || 'Sipariş bulunamadı');
    } finally {
      setExplainLoading(false);
    }
  };

  const Kart = ({ baslik, deger, alt, vurgu }: { baslik: string; deger: number | string; alt?: string; vurgu?: boolean }) => (
    <div className={`rounded-xl border p-4 ${vurgu ? 'border-orange-200 bg-orange-50' : 'border-slate-200 bg-white'}`}>
      <div className="text-xs font-medium text-slate-500">{baslik}</div>
      <div className={`mt-1 text-2xl font-bold tabular-nums ${vurgu ? 'text-orange-700' : 'text-slate-900'}`}>{deger}</div>
      {alt && <div className="mt-0.5 text-xs text-slate-400">{alt}</div>}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Üst çubuk */}
      <div className="flex flex-wrap items-center gap-3">
        <select value={days} onChange={(e) => setDays(Number(e.target.value))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
          <option value={7}>Son 7 gün</option>
          <option value={30}>Son 30 gün</option>
          <option value={90}>Son 90 gün</option>
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
          <option value="">Tüm turlar</option>
          <option value="auto">Otomatik</option>
          <option value="manual">Manuel</option>
        </select>
        <button onClick={load} disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white disabled:opacity-50">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Yenile
        </button>
      </div>

      {err && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4" /> {err}
        </div>
      )}

      {summary && (
        <>
          {/* Şu anki iş yükü */}
          <div>
            <h3 className="mb-2 text-sm font-semibold text-slate-700">Şu anki durum</h3>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <Kart baslik="Otomatik turun alacağı" deger={summary.pending.dueNow}
                alt="teslim edilmiş + eşiği dolmuş" vurgu />
              <Kart baslik="Teslim edilmiş, doğrulanmamış" deger={summary.pending.deliveredUnverified}
                alt="eşiği dolmayanlar dahil" />
              <Kart baslik="Okunamayan sipariş" deger={summary.pending.unreadable}
                alt={summary.pending.retryingNow > 0 ? `+${summary.pending.retryingNow} yeni` : 'listede kalmaya devam eder'} />
              <Kart baslik="Tur (dönem)" deger={summary.auto.runs + summary.manual.runs}
                alt={`${summary.auto.runs} otomatik · ${summary.manual.runs} manuel`} />
            </div>
          </div>

          {/* Turların ürettiği fayda */}
          <div>
            <h3 className="mb-2 text-sm font-semibold text-slate-700">
              Turlar ne yaptı <span className="font-normal text-slate-400">(son {summary.days} gün)</span>
            </h3>
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Tür</th>
                    <th className="px-4 py-2 text-right font-medium">Soruldu</th>
                    <th className="px-4 py-2 text-right font-medium">Güncellendi</th>
                    <th className="px-4 py-2 text-right font-medium">Doğrulandı</th>
                    <th className="px-4 py-2 text-right font-medium">Kapandı</th>
                    <th className="px-4 py-2 text-right font-medium">Atlandı</th>
                    <th className="px-4 py-2 text-right font-medium">Hata</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 tabular-nums">
                  {([['Otomatik', summary.auto], ['Manuel', summary.manual]] as const).map(([ad, t]) => (
                    <tr key={ad}>
                      <td className="px-4 py-2 font-medium text-slate-700">{ad}</td>
                      <td className="px-4 py-2 text-right">{t.asked}</td>
                      <td className="px-4 py-2 text-right">{t.updated}</td>
                      <td className="px-4 py-2 text-right font-semibold text-emerald-700">{t.verified}</td>
                      <td className="px-4 py-2 text-right">{t.finalized}</td>
                      <td className="px-4 py-2 text-right text-amber-700">{t.skipped}</td>
                      <td className="px-4 py-2 text-right text-red-600">{t.errors}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-slate-500">
              <strong>Doğrulandı</strong> = tur sayesinde ödenebilir hale gelen sipariş sayısı. Turun asıl faydası budur.
              Bu sayıyı eklenti değil sunucu hesaplar (veritabanındaki gerçek duruma bakarak).
              <br />
              <strong>Okunamayan sipariş</strong> = Amazon detay sayfası üst üste açılamayan siparişler. En sık sebep,
              siparişin kullanıcının <em>başka bir Amazon alıcı hesabından</em> verilmiş olması: tarayıcıda o an açık olan
              hesap o siparişi göremiyor. Bu yüzden listeden <em>düşürülmezler</em> — kullanıcı doğru hesapla girdiğinde
              okunup doğrulanırlar. Durumları ve kazançları etkilenmez.
            </p>
          </div>

          {/* Nasıl bitti + sürüm */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="mb-2 text-sm font-semibold text-slate-700">Turlar nasıl bitti</div>
              {summary.outcomes.length === 0 ? <div className="text-sm text-slate-400">Bu dönemde tur yok</div> : (
                <div className="flex flex-wrap gap-2">
                  {summary.outcomes.map((o) => (
                    <span key={o.outcome} className={`rounded-full border px-2.5 py-1 text-xs font-medium ${outcomeStyle(o.outcome)}`}>
                      {outcomeLabel(o.outcome)}: {o.count}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="mb-2 text-sm font-semibold text-slate-700">Eklenti sürümü</div>
              {summary.versions.length === 0 ? <div className="text-sm text-slate-400">Bu dönemde tur yok</div> : (
                <div className="flex flex-wrap gap-2">
                  {summary.versions.map((v) => (
                    <span key={v.version} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-600">
                      v{v.version}: {v.count}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Sipariş sorgulama */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-3 text-sm font-semibold text-slate-700">Bu sipariş neden doğrulanmadı?</div>
        <div className="flex flex-wrap gap-2">
          <input value={orderId} onChange={(e) => setOrderId(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sorgula()}
            placeholder="Sipariş numarası (111-1234567-1234567)"
            className="min-w-[260px] flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <input value={explainUserId} onChange={(e) => setExplainUserId(e.target.value)}
            placeholder="Kullanıcı ID (boş = kendi)"
            className="min-w-[220px] rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <button onClick={sorgula} disabled={explainLoading || !orderId.trim()}
            className="inline-flex items-center gap-2 rounded-lg bg-orange-500 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
            <Search className="h-4 w-4" /> Sorgula
          </button>
        </div>

        {explainErr && <div className="mt-3 text-sm text-red-600">{explainErr}</div>}

        {explain && (
          <div className="mt-4 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${stateStyle(explain.state)}`}>
                {explain.state}
              </span>
              <span className="text-sm text-slate-600">Durum: <strong>{explain.status || '—'}</strong></span>
              {explain.inAutoScope && (
                <span className="rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-xs text-orange-700">
                  Otomatik turun hedefinde
                </span>
              )}
              {explain.excludedFromPayment && (
                <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-600">
                  Ödeme kapsamı dışı
                </span>
              )}
            </div>

            <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-700">{explain.explain}</div>
            <div className="text-sm text-slate-500">→ {explain.next}</div>

            <div className="grid gap-2 text-xs text-slate-500 sm:grid-cols-3">
              <div>Sipariş: <strong className="text-slate-700">{fmtDate(explain.orderDateUtc)}</strong></div>
              <div>Son güncelleme: <strong className="text-slate-700">{fmtDate(explain.updatedAtUtc)}</strong></div>
              <div>Eşik: <strong className="text-slate-700">{fmtDate(explain.thresholdUtc)}</strong> ({explain.thresholdRule})</div>
            </div>

            {explain.statusHistory.length > 0 && (
              <div>
                <div className="mb-1 text-xs font-semibold text-slate-600">Durum geçmişi</div>
                <div className="space-y-1">
                  {explain.statusHistory.map((h, i) => (
                    <div key={i} className="text-xs text-slate-600">
                      {fmtDate(h.changedAtUtc)} · <span className="text-slate-400">{h.oldStatus || '—'}</span> → <strong>{h.newStatus || '—'}</strong>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Kullanıcı bazlı durum */}
      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-700">
          Kullanıcılar <span className="font-normal text-slate-400">— bekleyen işi olanlar ve turu hiç çalışmayanlar önce</span>
        </h3>
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Kullanıcı</th>
                <th className="px-4 py-2 text-right font-medium">Bekleyen</th>
                <th className="px-4 py-2 text-right font-medium">Tur</th>
                <th className="px-4 py-2 text-right font-medium">Doğrulanan</th>
                <th className="px-4 py-2 text-left font-medium">Son tur</th>
                <th className="px-4 py-2 text-left font-medium">Sonuç</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-400">Kayıt yok</td></tr>
              )}
              {users.map((u) => (
                <tr key={u.userId} className={u.runs === 0 && u.dueNow > 0 ? 'bg-amber-50/40' : ''}>
                  <td className="px-4 py-2 text-slate-700">{u.email}</td>
                  <td className="px-4 py-2 text-right font-semibold tabular-nums text-orange-700">{u.dueNow}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{u.runs}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-emerald-700">{u.verified}</td>
                  <td className="px-4 py-2 text-slate-500">{fmtDate(u.lastRunAtUtc)}</td>
                  <td className="px-4 py-2">
                    {u.lastOutcome
                      ? <span className={`rounded-full border px-2 py-0.5 text-xs ${outcomeStyle(u.lastOutcome)}`}>{outcomeLabel(u.lastOutcome)}</span>
                      : <span className="text-xs text-slate-400">hiç tur yok</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tur listesi */}
      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-700">
          Son turlar <span className="font-normal text-slate-400">({runsTotal} kayıt)</span>
        </h3>
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Başlangıç</th>
                <th className="px-3 py-2 text-left font-medium">Kullanıcı</th>
                <th className="px-3 py-2 text-left font-medium">Tür</th>
                <th className="px-3 py-2 text-left font-medium">Alıcı Hesabı</th>
                <th className="px-3 py-2 text-right font-medium">Hedef</th>
                <th className="px-3 py-2 text-right font-medium">Soruldu</th>
                <th className="px-3 py-2 text-right font-medium">Doğrulandı</th>
                <th className="px-3 py-2 text-right font-medium">Atlandı</th>
                <th className="px-3 py-2 text-left font-medium">Sonuç</th>
                <th className="px-3 py-2 text-left font-medium">Not</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {runs.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-6 text-center text-slate-400">
                  Bu dönemde tur kaydı yok
                </td></tr>
              )}
              {runs.map((r) => (
                <tr key={r.id}>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-500">{fmtDate(r.startedAtUtc)}</td>
                  <td className="px-3 py-2 text-slate-700">{r.email}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded px-1.5 py-0.5 text-xs ${r.type === 'auto' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                      {r.type === 'auto' ? 'Otomatik' : 'Manuel'}
                    </span>
                  </td>
                  <td className="px-3 py-2 max-w-[200px] truncate text-xs text-slate-600" title={r.buyerEmail || ''}>{r.buyerEmail || '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.targetCount}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.askedCount}</td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums text-emerald-700">{r.verifiedCount}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-amber-700">{r.skippedCount}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded-full border px-2 py-0.5 text-xs ${outcomeStyle(r.outcome)}`}>{outcomeLabel(r.outcome)}</span>
                  </td>
                  <td className="px-3 py-2 max-w-[220px] truncate text-xs text-slate-500" title={r.note || ''}>{r.note || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
